import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { API } from "@/lib/api";
import { DEFAULT_BANNER } from "@/components/admin/BannerManager";
import { Gift, Sparkles, ListChecks, Heart, ArrowRight, Cake, Snowflake, Gem, Flower2, Home as HomeIcon, GraduationCap } from "lucide-react";

// conservative, never rounded up: 408 -> "400+". Below 100 we make no claim.
const giftCountLabel = (n) => {
  if (!n || n < 100) return "Curated UK gifts";
  return `${Math.floor(n / 100) * 100}+ curated UK gifts`;
};

const occasions = [
  { icon: Cake, label: "Birthday" },
  { icon: Snowflake, label: "Christmas" },
  { icon: Gem, label: "Anniversary" },
  { icon: Flower2, label: "Mother's / Father's Day" },
  { icon: Heart, label: "Wedding" },
  { icon: HomeIcon, label: "Housewarming" },
  { icon: GraduationCap, label: "Graduation" },
];

const steps = [
  { icon: ListChecks, title: "Answer a few questions", body: "Tell us who it's for, the occasion and a little about them. Takes about 60 seconds." },
  { icon: Sparkles, title: "We match & explain", body: "Our engine scores our UK catalogue on interests, budget and vibe — then explains every pick." },
  { icon: Gift, title: "Get a curated shortlist", body: "A varied set of 8–10 gifts with reasons and links straight out to the retailer." },
];


const personas = [
  { name: "The Connoisseur", tag: "Wine · Whisky · Fine food", cat: "whisky", img: "https://images.pexels.com/photos/7135957/pexels-photo-7135957.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "The Homebody", tag: "Candles · Cosy · Spa", cat: "home", img: "https://images.unsplash.com/photo-1635887910118-11c5db92e034?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "The Tech Enthusiast", tag: "Gadgets · Audio · Smart", cat: "tech", img: "https://images.pexels.com/photos/33305814/pexels-photo-33305814.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "The Experience Seeker", tag: "Days out · Adventures", cat: "experience", img: "https://images.unsplash.com/photo-1507608616759-54f48f0af0ee?crop=entropy&cs=srgb&fm=jpg&q=85" },
];

const BANNER_CACHE = "cf_banner_src";

export default function Home() {
  const [stats, setStats] = useState(null);
  // remembered from the last visit, so an admin-chosen banner never flashes the default first
  const [hero, setHero] = useState(() => localStorage.getItem(BANNER_CACHE) || null);

  useEffect(() => {
    api.get("/stats/home").then((r) => setStats(r.data)).catch(() => {});
    api.get("/site/banner").then((r) => {
      const src = r.data?.url ? `${API.replace(/\/api$/, "")}${r.data.url}` : DEFAULT_BANNER;
      if (r.data?.url) localStorage.setItem(BANNER_CACHE, src);
      else localStorage.removeItem(BANNER_CACHE);
      setHero(src);
    }).catch(() => setHero((h) => h || DEFAULT_BANNER));
  }, []);

  const navigate = useNavigate();

  return (
    <div className="gm-fade">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 lg:px-10 pt-10 lg:pt-16 pb-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-6 gm-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-brand-amber font-semibold mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Thoughtful gifting, made effortless
            </span>
            <h1 className="font-serif font-semibold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-brand-ink">
              Find a gift they'll <span className="text-brand-green italic">actually love</span> in 60 seconds.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl">
              Answer a few quick questions about someone and get a curated, explained shortlist of
              gifts from favourite UK retailers. No account needed to start.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/quiz")} data-testid="hero-start-quiz-button"
                className="bg-brand-green hover:bg-brand-greendark rounded-full h-13 px-7 text-base gap-2 group h-12">
                Find My Gift <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button onClick={() => navigate("/browse")} variant="outline" data-testid="hero-browse-button"
                className="rounded-full h-12 px-7 text-base border-brand-green/30 hover:bg-brand-cream2">
                Browse gift ideas
              </Button>
            </div>
            {stats?.retailers?.length >= 3 && (
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground"
                data-testid="hero-curated-from">
                <span className="font-mono text-xs uppercase tracking-wider">Curated from</span>
                {stats.retailers.map((b) => (
                  <span key={b} className="font-serif italic text-brand-ink/70">{b}</span>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-6 gm-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border border-brand-cream2 gm-grain">
              {hero ? (
                <img src={hero} alt="Gifts wrapped in kraft paper and ribbon" fetchPriority="high"
                  data-testid="hero-image"
                  onError={() => { localStorage.removeItem(BANNER_CACHE); setHero(DEFAULT_BANNER); }}
                  className="w-full h-[380px] lg:h-[520px] object-cover" />
              ) : (
                <div data-testid="hero-image-placeholder"
                  className="w-full h-[380px] lg:h-[520px] bg-brand-cream2 animate-pulse" />
              )}
              <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-md rounded-2xl p-4 flex items-center gap-3 shadow-lg">
                <span className="w-11 h-11 rounded-full bg-brand-green flex items-center justify-center shrink-0">
                  <Gift className="w-5 h-5 text-brand-cream" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-brand-ink font-mono" data-testid="hero-gift-count">
                    {giftCountLabel(stats?.gift_count)}
                  </p>
                  <p className="text-xs text-muted-foreground">Matched to interests, budget & occasion</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Occasions */}
      <section className="max-w-7xl mx-auto px-4 lg:px-10 py-8">
        <p className="text-center text-sm text-muted-foreground mb-4">Perfect for every occasion</p>
        <div className="flex flex-wrap justify-center gap-3">
          {occasions.map((o) => (
            <div key={o.label} className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-full border border-brand-cream2 text-sm font-medium hover:border-brand-green/40 transition-colors">
              <o.icon className="w-4 h-4 text-brand-amber" /> {o.label}
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 lg:px-10 py-14">
        <h2 className="font-serif text-3xl lg:text-4xl text-center mb-3">How it works</h2>
        <p className="text-center text-muted-foreground mb-12 max-w-lg mx-auto">Three simple steps between you and a gift they'll remember.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.title} className="relative bg-white rounded-2xl p-8 border border-brand-cream2 hover:shadow-lg transition-shadow">
              <span className="absolute top-6 right-6 font-serif text-5xl text-brand-cream2">{i + 1}</span>
              <span className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center mb-5">
                <s.icon className="w-6 h-6 text-brand-green" />
              </span>
              <h3 className="font-serif text-xl mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Personas */}
      <section className="max-w-7xl mx-auto px-4 lg:px-10 py-8">
        <h2 className="font-serif text-3xl lg:text-4xl mb-2">Popular gift personas</h2>
        <p className="text-muted-foreground mb-8">Jump straight into a vibe, or take the full quiz for tailored picks.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {personas.map((p) => (
            <button key={p.name} onClick={() => navigate(`/browse?category=${p.cat}`)}
              data-testid={`persona-${p.cat}`}
              className="group relative rounded-2xl overflow-hidden h-56 text-left border border-brand-cream2">
              <img src={p.img} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-brand-ink/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="font-serif text-xl text-brand-cream">{p.name}</p>
                <p className="text-xs text-brand-cream/80 mt-1">{p.tag}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 lg:px-10 py-14">
        <div className="relative rounded-[2rem] bg-brand-green text-brand-cream p-10 lg:p-16 overflow-hidden gm-grain">
          <div className="relative max-w-xl">
            <h2 className="font-serif text-3xl lg:text-4xl mb-4 text-brand-cream">Ready to find the one?</h2>
            <p className="text-brand-cream/80 mb-8 text-lg">It's free, quick, and you don't need an account to begin.</p>
            <Button onClick={() => navigate("/quiz")} data-testid="cta-start-quiz-button"
              className="bg-brand-amber hover:bg-brand-amber/90 text-white rounded-full h-12 px-8 text-base gap-2 group">
              Start the quiz <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
